import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Upload, X } from "lucide-react";
import { api, fmtErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SubmitTask() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [form, setForm] = useState({ upi_id: "", feedback: "", comments: "" });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/tasks/${code}`).then((r) => setTask(r.data)).catch(() => setError("Task not found."));
  }, [code]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, upi_id: f.upi_id || user.upi_id || "" }));
  }, [user]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("task_id", task.id);
      fd.append("upi_id", form.upi_id);
      fd.append("feedback", form.feedback);
      fd.append("comments", form.comments);
      if (file) fd.append("screenshot", file);
      const { data } = await api.post("/user/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data.submission);
      toast.success("Submission received successfully.");
    } catch (err) {
      setError(fmtErr(err, "Submission failed"));
    } finally {
      setLoading(false);
    }
  };

  if (error && !task) return <div className="py-24 text-center text-slate-700 font-semibold" data-testid="submit-task-error">{error}</div>;
  if (!task || !user) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" data-testid="submit-loading" /></div>;

  if (done) {
    return (
      <div className="max-w-lg mx-auto py-12" data-testid="submission-success">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-8 text-center">
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
          <h1 className="mt-4 text-2xl font-bold font-heading text-slate-900">Submission received successfully.</h1>
          <p className="mt-2 text-sm text-slate-600">Your submission ID is <span className="font-bold text-slate-900" data-testid="submission-id-text">{done.submission_code}</span></p>
          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200" data-testid="submission-status-pending">Pending Verification</div>
          <p className="mt-4 text-sm text-slate-500">Our team will review your submission (usually within 24–72 hours). Track progress in My Submissions.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/account")} data-testid="go-to-submissions-btn" className="px-6 py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">My Submissions</button>
            <button onClick={() => navigate("/tasks")} data-testid="go-to-tasks-btn" className="px-6 py-3 rounded-xl font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors">More Tasks</button>
          </div>
        </div>
      </div>
    );
  }

  const roCls = "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-700";
  const inCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  return (
    <div className="max-w-2xl mx-auto" data-testid="submit-task-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Submit Task</h1>
      <p className="mt-1 text-sm text-slate-600">Share your honest experience with {task.app_name}. Reward: <strong className="text-blue-700">{inr(task.reward)}</strong></p>

      {error && <div data-testid="submit-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

      <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-7 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Your name</label>
            <input data-testid="submit-user-name" value={user.name} readOnly className={roCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">User ID</label>
            <input data-testid="submit-user-id" value={user.user_code} readOnly className={roCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Task ID</label>
            <input data-testid="submit-task-id" value={task.task_code} readOnly className={roCls} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Task name</label>
            <input data-testid="submit-task-name" value={task.app_name} readOnly className={roCls} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">UPI ID <span className="text-red-500">*</span></label>
          <input data-testid="submit-upi-input" required value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className={inCls} placeholder="yourname@upi" />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Your feedback / honest opinion <span className="text-red-500">*</span></label>
          <textarea data-testid="feedback-text-input" required rows={5} value={form.feedback} onChange={(e) => setForm({ ...form, feedback: e.target.value })}
            className={inCls} placeholder="Describe your genuine experience — what you liked, what didn't work, suggestions. Positive, neutral or negative feedback are all acceptable." />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Screenshot / proof of completion</label>
          {preview ? (
            <div className="relative inline-block">
              <img src={preview} alt="Proof preview" data-testid="screenshot-preview" className="h-44 rounded-xl border border-slate-200 object-cover" />
              <button type="button" data-testid="screenshot-remove-btn" onClick={() => { setFile(null); setPreview(""); }} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center shadow"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <label data-testid="screenshot-upload-label" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors">
              <Upload className="w-7 h-7 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Tap to upload screenshot (JPG/PNG, max 5 MB)</span>
              <input data-testid="screenshot-upload-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
            </label>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Additional comments <span className="text-slate-400 font-normal">(optional)</span></label>
          <textarea data-testid="comments-input" rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} className={inCls} placeholder="Anything else you'd like us to know" />
        </div>

        <button type="submit" data-testid="submit-feedback-btn" disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm text-base">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} SUBMIT TASK
        </button>
      </form>
    </div>
  );
}
